import React, { useEffect, useMemo, useState } from "react";
import { getRetestListDetails, getReportDetailsByRetestId } from "../../utils/api/endpoints/retests/retests";
import { CTBRetest } from "../../utils/api/endpoints/retests/parseRetests";
import { useNavigate, useSearchParams } from "react-router-dom";
import usePageTitle from "../../utils/hooks/usePageTitle";
import RetestCardGrid from "../../components/retests/Card/RetestCardGrid";
import LoadingSpinner from "../../components/retests/utils/LoadingSpinner";
import Pagination from "../../components/retests/utils/Pagination";
import { FiFilter, FiX, FiRefreshCw, FiSearch, FiChevronDown } from "react-icons/fi";
import MoreFilterIcon from "../../assets/icons/more_filter.svg";
import { useGetMyAcceptedDelegationsQuery } from "../../utils/api/endpoints/delegationsApi";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";

const RetestManagement: React.FC = () => {
  const [retests, setRetests] = useState<CTBRetest[]>([]);
  const [filteredRetests, setFilteredRetests] = useState<CTBRetest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [programFilter, setProgramFilter] = useState<string>("");
  const [programNamesList, setProgramNamesList] = useState<string[]>([]);
  const [retestToProgramMap, setRetestToProgramMap] = useState<Record<number, string>>({});
  const [showFilterPanel, setShowFilterPanel] = useState<boolean>(false);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState<"recent" | "oldest">("recent");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilterParam = searchParams.get("status");
  const cardsPerPage = 6;

  usePageTitle("Vulnerability Retests");
  const { role } = useUserCredentials();

  // Researcher-only tab state
  const [activeTab, setActiveTab] = useState<"mine" | "delegated">(
    role === UserRole.RESEARCHER && (searchParams.get("view") === "delegated") ? "delegated" : "mine"
  );

  // Delegated retests for researcher
  const { data: acceptedRetestsResp, isFetching: loadingDelegated } = useGetMyAcceptedDelegationsQuery(
    { scope: "retest" },
    { skip: role !== UserRole.RESEARCHER }
  );

  const delegatedRetests: CTBRetest[] = useMemo(() => {
    const items = acceptedRetestsResp?.data || [];
    return items
      .map(d => {
        const r: any = (d as any).Retest;
        const rep: any = r?.report;
        if (!r) return null;
        const obj: CTBRetest = {
          retest_id: r.retest_id ?? r.id,
          report_id: r.report_id,
          program_name: rep?.program_name || "",
          report_name: rep?.report_title || `Report #${r.report_id}`,
          status: r.status || "Pending",
          createdAt: r.createdAt || r.created_at || new Date().toISOString(),
          updatedAt: r.updatedAt || r.updated_at || r.created_at || new Date().toISOString(),
        };
        return obj;
      })
      .filter(Boolean) as CTBRetest[];
  }, [acceptedRetestsResp]);

  useEffect(() => {
    if (role !== UserRole.RESEARCHER) return;

    // If delegated retests are empty and user is on delegated tab, switch to mine
    if (delegatedRetests.length === 0 && activeTab === "delegated") {
      setActiveTab("mine");
      setSearchParams(prev => {
        prev.delete("view");
        return prev;
      });
      return;
    }

    setSearchParams(prev => {
      if (activeTab === "delegated") prev.set("view", "delegated");
      else prev.delete("view");
      return prev;
    });
  }, [activeTab, role, setSearchParams, delegatedRetests]);

  // Load retests with full details
  useEffect(() => {
    const fetchRetestsData = async () => {
      try {
        setLoading(true);
        const response = await getRetestListDetails();
        const data = response.data;
        setRetests(data);

        // Populate the program name map and program list from the aggregated data
        const programNames = new Set<string>();
        const retestProgramMap: Record<number, string> = {};

        data.forEach((retest: any) => {
          if (retest.program_name) {
            programNames.add(retest.program_name);
            retestProgramMap[retest.retest_id] = retest.program_name;
          }
        });

        setProgramNamesList(Array.from(programNames));
        setRetestToProgramMap(retestProgramMap);
      } catch (error) {
        console.error("Failed to fetch retests:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRetestsData();
  }, []);

  // Apply filters when retests or filters change
  useEffect(() => {
    let filtered = retests;

    // Apply status filter from URL params
    if (statusFilterParam) {
      filtered = filtered.filter(retest => retest.status === statusFilterParam);
    }

    // Apply status filter from dropdown
    if (statusFilter) {
      filtered = filtered.filter(retest => retest.status === statusFilter);
    }

    // Apply multiple status filter from advanced panel
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(retest => selectedStatuses.includes(retest.status));
    }

    // Apply program filter
    if (programFilter) {
      filtered = filtered.filter(retest => {
        const retestProgramName = retestToProgramMap[retest.retest_id] || retest.program_name;
        return retestProgramName === programFilter;
      });
    }

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(retest => {
        const reportName = retest.report_name?.toLowerCase() || "";
        const programName = (retestToProgramMap[retest.retest_id] || retest.program_name || "").toLowerCase();
        const description = retest.description?.toLowerCase() || "";
        return reportName.includes(query) || programName.includes(query) || description.includes(query);
      });
    }

    // Apply multiple month filter
    if (selectedMonths.length > 0) {
      filtered = filtered.filter(retest => {
        const date = new Date(retest.createdAt);
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const monthName = monthNames[date.getMonth()];
        return selectedMonths.includes(monthName);
      });
    }

    // Apply multiple year filter
    if (selectedYears.length > 0) {
      filtered = filtered.filter(retest => {
        const date = new Date(retest.createdAt);
        return selectedYears.includes(date.getFullYear().toString());
      });
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return orderBy === "recent" ? dateB - dateA : dateA - dateB;
    });

    setFilteredRetests(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [retests, statusFilter, programFilter, searchQuery, statusFilterParam, retestToProgramMap, selectedMonths, selectedYears, selectedStatuses, orderBy]);

  const handleCardClick = (retest: CTBRetest) => {
    navigate(`/dashboard/retests/${retest.retest_id}`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const response = await getRetestListDetails();
      setRetests(response.data);
    } catch (error) {
      console.error("Failed to fetch retests:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilter = () => {
    setSearchParams({});
    setStatusFilter("");
    setProgramFilter("");
    setSearchQuery("");
    setSelectedMonths([]);
    setSelectedYears([]);
    setSelectedStatuses([]);
  };

  const clearStatusFilter = () => {
    setSearchParams({});
    setStatusFilter("");
  };

  const clearProgramFilter = () => {
    setProgramFilter("");
  };

  // Get unique values for filters
  const uniqueStatuses = Array.from(new Set(retests.map(retest => retest.status).filter(Boolean)));

  const uniqueMonths = Array.from(new Set(
    retests.map(retest => {
      const date = new Date(retest.createdAt);
      const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      return monthNames[date.getMonth()];
    })
  )).sort();

  const uniqueYears = Array.from(new Set(
    retests.map(retest => {
      const date = new Date(retest.createdAt);
      return date.getFullYear().toString();
    })
  )).sort((a, b) => b.localeCompare(a));

  // Calculate pagination
  const totalPages = Math.ceil(filteredRetests.length / cardsPerPage);
  const startIndex = (currentPage - 1) * cardsPerPage;
  const endIndex = startIndex + cardsPerPage;
  const currentRetests = filteredRetests.slice(startIndex, endIndex);

  return (
    <div className="container mx-auto px-8 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-[#111827]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Vulnerability Retests
        </h1>

        <div className="flex items-center gap-4">
          {statusFilter && (
            <div className="flex items-center bg-blue-50 text-blue-700 px-4 py-2 rounded-md">
              <FiFilter className="mr-2" />
              <span>Filtered by: <strong>{statusFilter}</strong></span>
              <button
                onClick={clearStatusFilter}
                className="ml-3 p-1 hover:bg-blue-100 rounded-full"
                title="Clear status filter"
              >
                <FiX />
              </button>
            </div>
          )}
          {programFilter && (
            <div className="flex items-center bg-blue-50 text-blue-700 px-4 py-2 rounded-md">
              <FiFilter className="mr-2" />
              <span>Filtered by program: <strong>{programFilter}</strong></span>
              <button
                onClick={clearProgramFilter}
                className="ml-3 p-1 hover:bg-blue-100 rounded-full"
                title="Clear program filter"
              >
                <FiX />
              </button>
            </div>
          )}

          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-white text-[#374151] border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          >
            <FiRefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Search and Filters Section */}
      <div className="mb-6">
        <div className="flex gap-3 items-center">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by vulnerability name, description or category"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-[#374151]"
            />
          </div>
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 pr-10 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-[#374151] appearance-none cursor-pointer w-full"
            >
              <option value="">All Statuses</option>
              {uniqueStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500 w-4 h-4" />
          </div>
          <div className="relative">
            <select
              value={programFilter}
              onChange={(e) => setProgramFilter(e.target.value)}
              className="px-4 py-2.5 pr-10 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-[#374151] appearance-none cursor-pointer w-full"
            >
              <option value="">All Programs</option>
              {programNamesList.map((program) => (
                <option key={program} value={program}>
                  {program}
                </option>
              ))}
            </select>
            <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500 w-4 h-4" />
          </div>
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white shadow-sm hover:bg-gray-50 flex items-center gap-2 text-[#374151] transition-colors"
          >
            <img src={MoreFilterIcon} alt="Filter" className="w-4 h-4" />
            <span>More Filters</span>
          </button>
        </div>
      </div>

      {/* Researcher-only tabs for delegated retests */}
      {role === UserRole.RESEARCHER && delegatedRetests.length > 0 && (
        <div className="mb-4 border-b border-gray-200">
          <nav className="-mb-px flex space-x-6">
            <button
              className={`whitespace-nowrap border-b-2 px-1 pb-2 text-sm font-medium ${activeTab === "mine" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              onClick={() => setActiveTab("mine")}
            >
              My Retests
            </button>
            <button
              className={`whitespace-nowrap border-b-2 px-1 pb-2 text-sm font-medium ${activeTab === "delegated" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              onClick={() => setActiveTab("delegated")}
            >
              Delegated Retests
            </button>
          </nav>
        </div>
      )}

      {/* Advanced Filter Panel */}
      {showFilterPanel && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg shadow-lg p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Status Section */}
            <div>
              <h3 className="font-bold text-[#111827] mb-3">Status</h3>
              <div className="space-y-2">
                {uniqueStatuses.map((status) => (
                  <label key={status} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStatuses([...selectedStatuses, status]);
                        } else {
                          setSelectedStatuses(selectedStatuses.filter(s => s !== status));
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-[#111827]">{status}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Month Section */}
            <div>
              <h3 className="font-bold text-[#111827] mb-3">Month</h3>
              <div className="space-y-2">
                {uniqueMonths.map((month) => (
                  <label key={month} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedMonths.includes(month)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedMonths([...selectedMonths, month]);
                        } else {
                          setSelectedMonths(selectedMonths.filter(m => m !== month));
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-[#111827]">{month}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Year Section */}
            <div>
              <h3 className="font-bold text-[#111827] mb-3">Year</h3>
              <div className="space-y-2">
                {uniqueYears.map((year) => (
                  <label key={year} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedYears.includes(year)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedYears([...selectedYears, year]);
                        } else {
                          setSelectedYears(selectedYears.filter(y => y !== year));
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-[#111827]">{year}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Order Section */}
            <div>
              <h3 className="font-bold text-[#111827] mb-3">Order</h3>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="order"
                    checked={orderBy === "recent"}
                    onChange={() => setOrderBy("recent")}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-[#111827]">Recent</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="order"
                    checked={orderBy === "oldest"}
                    onChange={() => setOrderBy("oldest")}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-[#111827]">Oldest</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {activeTab === "delegated" && role === UserRole.RESEARCHER ? (
        loadingDelegated ? (
          <LoadingSpinner />
        ) : (
          <RetestCardGrid retests={delegatedRetests} onCardClick={handleCardClick} />
        )
      ) : loading ? (
        <LoadingSpinner />
      ) : filteredRetests.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-lg text-gray-500 mb-4">
            {statusFilter ? `No retests found with status "${statusFilter}".` : "No retests found."}
          </p>
          {statusFilter && (
            <button
              onClick={clearFilter}
              className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors"
            >
              Clear filter
            </button>
          )}
        </div>
      ) : (
        <>
          <RetestCardGrid retests={currentRetests} onCardClick={handleCardClick} />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}

          {/* Results count */}
          <div className="mt-4 text-center text-gray-600">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredRetests.length)} of {filteredRetests.length} results
          </div>
        </>
      )}
    </div>
  );
};

export default RetestManagement;
